import { Slot } from '@radix-ui/react-slot';
import { Link } from '@tanstack/react-router';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import type { IconName } from 'lucide-react/dynamic';
import * as React from 'react';
import { CONFIG } from '@/config-globals';
import { cn } from '@/lib/utils';
import { Icon } from './icon';

function Breadcrumb({ ...props }: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        'text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm break-words sm:gap-2.5',
        className,
      )}
      {...props}
    />
  );
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn('inline-flex items-center gap-1.5', className)}
      {...props}
    />
  );
}

function BreadcrumbLink({
  asChild,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
}) {
  const Comp = asChild ? Slot : Link;

  return (
    <Comp
      data-slot="breadcrumb-link"
      className={cn('hover:text-foreground transition-colors', className)}
      to={props.href as string}
      {...props}
    />
  );
}

function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn('text-foreground font-normal', className)}
      {...props}
    />
  );
}

function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn('[&>svg]:size-3.5', className)}
      {...props}
    >
      {children ?? <ChevronRight />}
    </li>
  );
}

function BreadcrumbEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontal className="size-4" />
      <span className="sr-only">More</span>
    </span>
  );
}

interface BreadcrumbItem {
  label: string;
  route?: string;
  active?: boolean;
  onClick?: () => void;
}

export type BreadcrumbsProps = {
  title?: string | React.ReactNode;
  icon?: IconName;
  action?: any;
  items: BreadcrumbItem[];
};

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ title, icon, action, items }) => {
  return (
    <nav className="flex flex-wrap w-full">
      <div className="flex flex-wrap mb-1 w-full items-center content-center justify-between">
        <span className="flex flex-wrap items-center content-center text-2xl md:text-3xl font-extrabold tracking-tight text-gray-800 dark:text-gray-100">
          {icon && <Icon name={icon} className="w-6 h-6 mr-2 text-primary" />}
          {title}
        </span>
        {action}
      </div>
      <div className="mb-5 flex flex-wrap flex-row items-center content-center w-full">
        <Breadcrumb>
          <BreadcrumbList>
            {items.length ? (
              <>
                <BreadcrumbItem key="home">
                  <BreadcrumbLink href="/">
                    <Icon name="home" />
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {items.map((item, index) => (
                  <React.Fragment key={index}>
                    <BreadcrumbSeparator />
                    {item.active ? (
                      <BreadcrumbItem>
                        <BreadcrumbPage>{item.label}</BreadcrumbPage>
                      </BreadcrumbItem>
                    ) : (
                      <BreadcrumbItem>
                        <BreadcrumbLink
                          href={item.route ? `/${item.route}` : '#'}
                          onClick={item?.onClick ?? undefined}
                        >
                          {item.label}
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                    )}
                  </React.Fragment>
                ))}
              </>
            ) : null}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </nav>
  );
};

export const BreadcrumbTrail: React.FC<Pick<BreadcrumbsProps, 'items'>> = ({ items }) => {
  return (
    <Breadcrumb>
      <BreadcrumbList className="flex-nowrap text-[11px] gap-1 sm:gap-1">
        <BreadcrumbItem key="home">
          <BreadcrumbLink href="/" className="font-semibold text-foreground-2">
            {CONFIG.APP_NAME}
          </BreadcrumbLink>
        </BreadcrumbItem>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            <BreadcrumbSeparator />
            {item.active ? (
              <BreadcrumbItem>
                <BreadcrumbPage className="text-[11px]">{item.label}</BreadcrumbPage>
              </BreadcrumbItem>
            ) : (
              <BreadcrumbItem>
                <BreadcrumbLink
                  href={item.route ? `/${item.route}` : '#'}
                  onClick={item?.onClick ?? undefined}
                >
                  {item.label}
                </BreadcrumbLink>
              </BreadcrumbItem>
            )}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Breadcrumbs,
};
